import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/Tabs';
import { 
  TrendingUp, 
  Globe, 
  Smartphone, 
  Monitor, 
  MapPin, 
  Clock,
  ExternalLink,
  Activity,
  Users,
  Eye,
  MousePointer,
  Calendar,
  Tablet
} from 'lucide-react';
import { useAnalytics } from '../../hooks/useAnalytics';

interface TrafficAnalysisProps {
  dateRange: {
    from: Date;
    to: Date;
  };
  projectId?: string;
}

export function TrafficAnalysis({ dateRange, projectId }: TrafficAnalysisProps) {
  const [activeTab, setActiveTab] = useState('sources');
  const analytics = useAnalytics(projectId);
  const { data: trafficData, isLoading, error } = analytics.useTraffic(dateRange);
  
  const dateRangeText = `${dateRange.from.toLocaleDateString()} - ${dateRange.to.toLocaleDateString()}`;

  // Process traffic data from the new endpoint
  const traffic = trafficData?.traffic;
  const dataSource = trafficData?.dataSource;

  // Process traffic sources with real data
  const trafficSources = traffic?.trafficSources || [];

  // Process device breakdown with real data
  const deviceTypes = (traffic?.deviceBreakdown || []).map((device: any) => {
    const totalSessions = traffic?.totalSessions || 1;
    return {
      device: device.device,
      sessions: device.sessions,
      users: device.users,
      pageviews: device.pageviews,
      bounceRate: device.bounceRate,
      percentage: totalSessions > 0 ? ((device.sessions / totalSessions) * 100).toFixed(1) : '0',
      icon: device.device?.toLowerCase() === 'mobile' ? Smartphone : 
            device.device?.toLowerCase() === 'tablet' ? Tablet : Monitor
    };
  });

  // Process countries with real data and flags
  const topCountries = traffic?.countries || [];

  // Process referrers with real data
  const topReferrers = traffic?.referrers || [];

  // Process cities
  const topCities = traffic?.cities || [];

  // Process browsers and operating systems
  const browsers = traffic?.browsers || [];
  const operatingSystems = traffic?.operatingSystems || [];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Traffic Analysis</h2>
            <p className="text-sm text-gray-600">Detailed breakdown of your website traffic</p>
          </div>
          <div className="text-sm text-gray-500">{dateRangeText}</div>
        </div>
        <div className="animate-pulse">
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded mb-6"></div>
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Traffic Analysis</h2>
            <p className="text-sm text-gray-600">Detailed breakdown of your website traffic</p>
          </div>
          <div className="text-sm text-gray-500">{dateRangeText}</div>
        </div>
        <Card className="p-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700">
          <div className="text-center">
            <p className="text-red-600 dark:text-red-400">Error loading traffic analysis data</p>
            <p className="text-sm text-red-500 dark:text-red-400 mt-1">{error.message}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Traffic Analysis</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">Comprehensive breakdown of your website traffic sources</p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {dateRangeText} {projectId && `• Project: ${projectId}`}
          </div>
          {dataSource && (
            <Badge variant={dataSource === 'ga4_real' ? 'success' : 'secondary'}>
              {dataSource === 'ga4_real' ? 'Real GA4 Data' : 'Sample Data'}
            </Badge>
          )}
        </div>
      </div>

      {/* Data Source Indicator */}
      {dataSource === 'ga4_real' && (
        <Card className="p-4 bg-gradient-to-r from-green-50 dark:from-green-900/20 to-blue-50 dark:to-blue-900/20 border-green-200 dark:border-green-700">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mr-3">
              <Activity className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Live GA4 Traffic Data</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Showing comprehensive traffic analysis from Google Analytics 4
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Traffic Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Sessions</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {traffic?.totalSessions?.toLocaleString() || '0'}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Traffic Sources</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {trafficSources.length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <Globe className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Countries</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {topCountries.length}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <MapPin className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Referrers</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {topReferrers.length}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <ExternalLink className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Traffic Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="sources">Sources</TabsTrigger>
          <TabsTrigger value="devices">Devices</TabsTrigger>
          <TabsTrigger value="locations">Locations</TabsTrigger>
          <TabsTrigger value="referrers">Referrers</TabsTrigger>
        </TabsList>

        <TabsContent value="sources" className="space-y-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Traffic Sources</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Source</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Sessions</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Users</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-gray-300">New Users</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Bounce Rate</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Avg. Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {trafficSources.map((source: any, index: number) => (
                    <tr key={index} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          <Globe className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="font-medium text-gray-900 dark:text-white">{source.source}</span>
                          <Badge variant="outline" className="ml-2 text-xs">
                            {source.percentage}%
                          </Badge>
                        </div>
                      </td>
                      <td className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">
                        {source.sessions?.toLocaleString() || '0'}
                      </td>
                      <td className="text-right py-3 px-4 text-gray-900 dark:text-white">
                        {source.users?.toLocaleString() || '0'}
                      </td>
                      <td className="text-right py-3 px-4 text-gray-900 dark:text-white">
                        {source.newUsers?.toLocaleString() || '0'}
                      </td>
                      <td className="text-right py-3 px-4 text-gray-900 dark:text-white">
                        {source.bounceRate}%
                      </td>
                      <td className="text-right py-3 px-4 text-gray-900 dark:text-white">
                        {source.avgDuration}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {trafficSources.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No traffic source data available for this period
                </div>
              )}
            </div>
            <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              {dataSource === 'ga4_real' ? 'Live GA4 data' : 'Sample data'} • 
              Total sessions: {traffic?.totalSessions?.toLocaleString() || '0'}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="devices" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {deviceTypes.map((device: any, index: number) => (
              <Card key={index} className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <device.icon className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-2" />
                    <h3 className="font-semibold text-gray-900 dark:text-white">{device.device}</h3>
                  </div>
                  <Badge variant="outline">{device.percentage}%</Badge>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Sessions</span>
                    <span className="font-medium text-gray-900 dark:text-white">{device.sessions?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Users</span>
                    <span className="font-medium text-gray-900 dark:text-white">{device.users?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Pageviews</span>
                    <span className="font-medium text-gray-900 dark:text-white">{device.pageviews?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Bounce Rate</span>
                    <span className="font-medium text-gray-900 dark:text-white">{device.bounceRate}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-3">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${device.percentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {dataSource === 'ga4_real' ? 'Live GA4 data' : 'Sample data'}
                </div>
              </Card>
            ))}
          </div>
          {deviceTypes.length === 0 && (
            <Card className="p-6">
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No device data available for this period
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="locations" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Countries */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Countries</h3>
              <div className="space-y-4">
                {topCountries.slice(0, 10).map((country: any, index: number) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <span className="text-2xl mr-3">{country.flag}</span>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">{country.country}</p>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full" 
                            style={{ width: `${country.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    <div className="ml-4 text-right">
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {country.sessions?.toLocaleString() || '0'}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{country.percentage}%</p>
                    </div>
                  </div>
                ))}
                {topCountries.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No country data available for this period
                  </div>
                )}
              </div>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                {dataSource === 'ga4_real' ? 'Real GA4 country data' : 'Estimated from total sessions'}
              </div>
            </Card>

            {/* Cities */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Cities</h3>
              <div className="space-y-4">
                {topCities.slice(0, 10).map((city: any, index: number) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <MapPin className="w-4 h-4 text-gray-400 mr-3" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">{city.city}</p>
                      </div>
                    </div>
                    <div className="ml-4 text-right">
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {city.sessions?.toLocaleString() || '0'}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {city.users?.toLocaleString() || '0'} users
                      </p>
                    </div>
                  </div>
                ))}
                {topCities.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No city data available for this period
                  </div>
                )}
              </div>
              <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                {dataSource === 'ga4_real' ? 'Real GA4 city data' : 'Sample data'}
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="referrers" className="space-y-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Referrers</h3>
            <div className="space-y-4">
              {topReferrers.map((referrer: any, index: number) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <ExternalLink className="w-4 h-4 text-gray-400 mr-3" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">{referrer.domain}</p>
                      {referrer.referrer && referrer.referrer !== referrer.domain && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{referrer.referrer}</p>
                      )}
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                        <div 
                          className="bg-purple-600 h-2 rounded-full" 
                          style={{ width: `${referrer.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 text-right">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {referrer.sessions?.toLocaleString() || '0'}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{referrer.percentage}%</p>
                  </div>
                </div>
              ))}
              {topReferrers.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No referrer data available for this period
                </div>
              )}
            </div>
            <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              {dataSource === 'ga4_real' ? 'Real GA4 referrer data' : 'Estimated from total sessions'}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
} 